// @ts-nocheck
import React, {useEffect, useMemo, useState} from 'react';
import {Card, CardContent, CardHeader, Divider, Grid, TextField, Typography} from '@mui/material';
import {t} from 'ttag';
import {useFormContext, useWatch} from 'react-hook-form';
import Amount from './Amount';
import {DataGrid} from '@mui/x-data-grid';
import table from '../../../../../../../../../../components/common/styles';

const columns = [
  {
    field: 'pnr',
    headerName: t`PNR`,
    width: 150,
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  },
  {
    field: 'ticket',
    headerName: t`Ticket`,
    width: 150,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    cellClassName: 'secondary-text'
  },
  {
    field: 'fare',
    headerName: t`Fare`,
    width: 150,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({value}) => <Amount value={value?.currentValue} currency={value?.currency} />,
    cellClassName: 'bold'
  },
  {
    field: 'total_taxes',
    headerName: t`Total Taxes`,
    width: 150,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({value}) => <Amount value={value?.currentValue} currency={value?.currency} />,
    cellClassName: 'bold'
  },
  {
    field: 'total_amount',
    headerName: t`Total Amount`,
    width: 150,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({value}) => <Amount value={value?.currentValue} currency={value?.currency} />,
    cellClassName: 'bold'
  }
];

const formatCurrency = (val) => {
  const num = parseFloat(String(val ?? 0).replace(',', '.'));
  if (isNaN(num)) return '0,00';
  return num.toFixed(2).replace('.', ',');
};

const parseCurrency = (str) => {
  if (!str) return 0;
  return Math.max(0, parseFloat(String(str).replace(',', '.')) || 0);
};

const filterCurrencyInput = (value) => {
  // normalize dot to comma, allow only digits and one comma
  const normalized = value.replace(/\./g, ',').replace(/[^0-9,]/g, '');
  const parts = normalized.split(',');
  return parts.length > 2 ? parts[0] + ',' + parts.slice(1).join('') : normalized;
};

const RefundSubjectInfo = ({editable = true}) => {
  const {setValue} = useFormContext();
  const [reservationCode, companyCode, documentNumber, amounts, manual] = useWatch({
    name: ['reservationCode', 'companyCode', 'documentNumber', 'amounts', 'manual']
  });

  const totalFareRefund = useMemo(() => amounts?.find((a) => a.type === 'totalFareRefund') || {}, [amounts]);
  const totalPaidTaxes = useMemo(() => amounts?.find((a) => a.type === 'totalPaidTaxes') || {}, [amounts]);
  const totalAmount = useMemo(() => {
    const explicit = amounts?.find((a) => a.type === 'totalAmount');
    if (explicit) return explicit;
    const fare = amounts?.find((a) => a.type === 'totalFarePaid');
    const taxes = amounts?.find((a) => a.type === 'totalPaidTaxes');
    const computed = ((fare?.currentValue ?? 0) + (taxes?.currentValue ?? 0));
    return {currentValue: computed, currency: fare?.currency ?? taxes?.currency ?? null};
  }, [amounts]);

  const [fareDisplay, setFareDisplay] = useState('0');
  const [taxesDisplay, setTaxesDisplay] = useState('0');
  const [editingFare, setEditingFare] = useState(false);
  const [editingTaxes, setEditingTaxes] = useState(false);

  useEffect(() => {
    if (!editingFare) setFareDisplay(formatCurrency(totalFareRefund.currentValue ?? 0));
  }, [totalFareRefund.currentValue, editingFare]);

  useEffect(() => {
    if (!editingTaxes) setTaxesDisplay(formatCurrency(totalPaidTaxes.currentValue ?? 0));
  }, [totalPaidTaxes.currentValue, editingTaxes]);

  const updateAmountField = (type, rawValue) => {
    const numValue = Math.max(0, parseFloat(rawValue) || 0);
    const fare = type === 'totalFareRefund' ? numValue : (amounts?.find(a => a.type === 'totalFareRefund')?.currentValue || 0);
    const taxes = type === 'totalPaidTaxes' ? numValue : (amounts?.find(a => a.type === 'totalPaidTaxes')?.currentValue || 0);
    const total = Math.round((fare + taxes) * 100) / 100;
    setValue('amounts', amounts.map(a => {
      if (a.type === type) return {...a, currentValue: numValue, modified: true};
      if (type === 'totalFareRefund' && a.type === 'totalFarePaid') return {...a, currentValue: numValue, modified: true};
      if (a.type === 'totalAmount') return {...a, currentValue: total, modified: true};
      return a;
    }), {shouldValidate: true});
  };

  if (manual) {
    return (
      <Card elevation={3}>
        <CardHeader title={<Typography fontFamily={'OpenSansMedium'}>{t`Basic info`}</Typography>} />
        <Divider/>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} md={4}>
              <TextField
                label={t`PNR`}
                value={reservationCode || ''}
                onChange={e => setValue('reservationCode', e.target.value)}
                size={'small'}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                label={t`TKT`}
                value={`${companyCode || ''} ${documentNumber || ''}`}
                size={'small'}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4} />
            <Grid item xs={6} md={4}>
              <TextField
                label={t`Fare`}
                value={fareDisplay}
                onChange={e => setFareDisplay(filterCurrencyInput(e.target.value))}
                onFocus={() => {
                  setEditingFare(true);
                  if (fareDisplay === '0' || fareDisplay === '0,00') setFareDisplay('');
                }}
                onBlur={() => {
                  const num = parseCurrency(fareDisplay);
                  setFareDisplay(formatCurrency(num));
                  setEditingFare(false);
                  updateAmountField('totalFareRefund', num);
                }}
                size={'small'}
                disabled={!editable}
                required={editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                label={t`Total taxes`}
                value={taxesDisplay}
                onChange={e => setTaxesDisplay(filterCurrencyInput(e.target.value))}
                onFocus={() => {
                  setEditingTaxes(true);
                  if (taxesDisplay === '0' || taxesDisplay === '0,00') setTaxesDisplay('');
                }}
                onBlur={() => {
                  const num = parseCurrency(taxesDisplay);
                  setTaxesDisplay(formatCurrency(num));
                  setEditingTaxes(false);
                  updateAmountField('totalPaidTaxes', num);
                }}
                size={'small'}
                disabled={!editable}
                required={editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                label={t`Total amount`}
                value={formatCurrency(totalAmount.currentValue ?? 0)}
                size={'small'}
                disabled
                fullWidth
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={3}>
      <CardHeader title={<Typography fontFamily={'OpenSansMedium'}>{t`Basic info`}</Typography>} />
      <Divider/>
      <CardContent>
        <DataGrid
          columns={columns}
          rows={[
            {
              id: 1,
              pnr: reservationCode,
              ticket: `${companyCode} ${documentNumber}`,
              fare: totalFareRefund,
              total_taxes: totalPaidTaxes,
              total_amount: totalAmount
            }
          ]}
          density={'compact'}
          disableRowSelectionOnClick
          hideFooter
          sx={table}
        />
      </CardContent>
    </Card>
  );
};

export default RefundSubjectInfo;